import { useState } from 'react';

import { useScrapingIntents } from '../../model/use-scraping-intents';
import {
  ScrapingEmptyState,
  ScrapingErrorState,
  ScrapingTableLoadingState,
} from '../../ui/scraping-section-state';
import { ScrapingPagination } from '../../ui/scraping-pagination';
import { ScrapingIntentTable } from '../scraping-intent/scraping-intent-table';
import { Select, SelectContent, SelectTrigger, SelectValue, SelectItem } from '@syncly/ui/select';
import { SearchInput } from '@/shared/components/search-input';
import { PlusIcon } from 'lucide-react';
import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter';
import { overlay } from 'overlay-kit';
import { GoogleSearchScrapingDialog } from './google-search-scraping.dialog';
import { SCRAPING_INTENT_MODE, SCRAPING_INTENT_SELECTOR_TYPE } from '../../types';
import { Stage } from '@/shared/components/stage/stage';
import { Button } from '@/shared/components/shadcn/button';

export function GoogleSearchScrapingList() {
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack.at(-1);
  const [platformFilter, setPlatformFilter] = useState<SOURCE_PLATFORM_TYPE | undefined>(undefined);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [mode, setMode] = useState<undefined | SCRAPING_INTENT_MODE>(undefined);
  const [ownerSearch, setOwnerSearch] = useState<string>('');

  const intentsQuery = useScrapingIntents({
    type: SCRAPING_INTENT_SELECTOR_TYPE.KEYWORD,
    limit: 10,
    cursor,
    owner: ownerSearch,
    platform: platformFilter,
    keyword: searchKeyword,
    mode: mode,
  });

  const nextCursor = intentsQuery.data?.page.next_cursor;

  const handleSearch = (keyword: string) => {
    setSearchKeyword(keyword);
    setCursorStack([]); // Reset to first page when searching
  };

  const handleOwnerSearch = (owner: string) => {
    setOwnerSearch(owner);
    setCursorStack([]);
  };

  const handleCreateNew = () => {
    overlay.open(({ isOpen, close, unmount }) => {
      return (
        <GoogleSearchScrapingDialog
          isOpen={isOpen}
          onClose={close}
          onConfirm={() => {}}
          onExit={unmount}
        />
      );
    });
  };

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-6">
        <div className="flex flex-1 flex-col gap-1">
          <p className="text-xl font-semibold">
            Scraping list ({intentsQuery.data?.page.total_count || 0})
          </p>
          <p className="text-muted-foreground text-sm">
            View and manage your scraping requests. Track the progress of each request and stay
            updated.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <Select
            value={platformFilter || 'all'}
            onValueChange={(value) => {
              if (value === 'all') {
                setPlatformFilter(undefined);
              } else {
                setPlatformFilter(value as SOURCE_PLATFORM_TYPE);
              }
            }}
          >
            <SelectTrigger className="w-fit min-w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={'all'}>All</SelectItem>
              <SelectItem value={SOURCE_PLATFORM_TYPE.TIKTOK}>TikTok</SelectItem>
              <SelectItem value={SOURCE_PLATFORM_TYPE.INSTAGRAM}>Instagram</SelectItem>
              {/* <SelectItem value={SOURCE_PLATFORM_TYPE.YOUTUBE}>YouTube</SelectItem> */}
            </SelectContent>
          </Select>
          <SearchInput placeholder="Search by keyword" onSubmit={handleSearch} />
          <SearchInput placeholder="Search by owner" onSubmit={handleOwnerSearch} />
          <Button onClick={handleCreateNew} variant="default">
            <PlusIcon />
            Create new
          </Button>
        </div>
      </div>
      <Stage>
        <Stage.Scene status={intentsQuery.isPending}>
          <ScrapingTableLoadingState />
        </Stage.Scene>
        <Stage.Scene status={intentsQuery.isError}>
          <ScrapingErrorState onRetry={() => void intentsQuery.refetch()} />
        </Stage.Scene>
        <Stage.Scene status={intentsQuery.data?.items.length === 0}>
          <ScrapingEmptyState />
        </Stage.Scene>
        <Stage.Scene default>
          <ScrapingIntentTable
            intents={intentsQuery.data?.items || []}
            kind="google-search"
            mode={mode}
            setMode={setMode}
          />
        </Stage.Scene>
      </Stage>

      <ScrapingPagination
        hasMore={!!intentsQuery.data?.page.has_more}
        canGoBack={cursorStack.length > 0}
        isFetching={intentsQuery.isFetching}
        onNext={() => {
          if (nextCursor != null) {
            setCursorStack((previous) => [...previous, nextCursor]);
          }
        }}
        onBack={() => setCursorStack((previous) => previous.slice(0, -1))}
      />
    </section>
  );
}
